import classNames from 'classnames'
import React, { useState } from "react"
import { useBeforeunload } from 'react-beforeunload'
import { useDispatch } from "react-redux"
import { Button, CustomInput, FormGroup, Input, Label } from "reactstrap"
import { FightSegmentList } from 'web/components/fight-segments/FightSegmentList'
import { GuiData } from 'web/data/gui-data'
import { FightEvent, FightSegment } from 'web/data/models'
import { useInputValue } from "web/hooks/useInputValue"
import { AppActions, RenderMode } from "web/redux/slices/app-slice"
import { useEditFight_BadName } from 'web/redux/slices/edit-fight-slice'
import { FightActions } from "web/redux/slices/fight-slice"
import { ModalActions } from "web/redux/slices/modal-slice"
import { SetupActions, useSetupSelectors } from 'web/redux/slices/setup-slice'
import { createGroupedOptions } from "web/utils/jsx-utils"
import { dataTest } from 'web/utils/test-utils'
import { decorateNameWithOrdinal } from 'web/utils/text-utils'
import { secondsToTimestamp } from 'web/utils/time-utils'
import { assertDefined, expectOneById, groupList, joinLines, lastItem } from "web/utils/utils"

export const EditFightContainer: React.FC = () => {

    useBeforeunload(event => event.preventDefault())

    const disp = useDispatch()
    const [editFightSelectors, editFightActions, state] = useEditFight_BadName()

    const activeFight = state.fight
    assertDefined(activeFight)

    const [fightName, setFightName, setFightNameOnBlur] = useInputValue(activeFight.name)
    const [fightNote, setFightNote] = useState(activeFight.note)
    const [fightCategory, setFightCategory] = useState(activeFight.categoryId)

    const fightCategoryOptions = createGroupedOptions({
        optionGroups: GuiData.FightCategoryOptions,
        onlyIncludeSelectable: true
    })

    const fightSetupSelectors = useSetupSelectors()

    const hasAssignments = fightSetupSelectors.getSetupsForFight(activeFight.id).length > 0

    const handleSetNote = () => {
        disp(ModalActions.openTextAreaModal({
            title: "Set fight note",
            text: fightNote,
            onConfirm: (note) => setFightNote(note)
        }))
    }

    const handleSaveChanges = () => {
        disp(FightActions.updateFight({
            fight: {
                ...activeFight,
                name: fightName,
                note: fightNote,
                categoryId: fightCategory
            },
            segments: state.segments,
            events: state.events
        }))

        // We might have lost setup-events due to segments being
        // removed, to account for this we'll pour everything that
        // wasn't lost back into their respective setups.
        const eventsPerSetup = groupList(state.setupFightEvents, (item => item.setupId))
        const setupIds = new Set(state.setupFightEvents.flatMap(ev => ev.setupId))

        disp(SetupActions.removeOrphanAssignmentsAndUpdateSetupFightEvents({
            fightId: activeFight.id,
            eventIds: {
                fromFight: state.events.map(e => e.id),
                fromFightSetups: state.setupFightEvents.map(e => e.id)
            },
            eventSetupData: {
                setupIds,
                eventsPerSetup
            }
        }))

        // All done, save and leave.

        disp(editFightActions.endEdit())
        disp(AppActions.setRenderMode({ renderMode: RenderMode.Display }))
    }

    const handleCancelEdit = () => {
        disp(editFightActions.endEdit())
        disp(AppActions.setRenderMode({ renderMode: RenderMode.Display }))
    }

    const handleEditEvent = (segment: FightSegment, event: FightEvent) => {
        disp(ModalActions.openEditEventModal({ segment, event }))
    }

    const handleDeleteEvent = (eventId: string, skipConfirm: boolean) => {
        const doDelete = () => disp(editFightActions.deleteEvent({ eventId }))

        if (skipConfirm) {
            doDelete()
        } else {

            disp(ModalActions.openConfirmModal({
                title: "Delete event?",
                btnColor: "danger",
                content: (
                    <>
                        <p>{"You sure?"}</p>
                        <p className="mb-0 small text-muted">
                            {"Tip: you can shift+click delete an event to skip this confirmation."}
                        </p>
                    </>
                ),
                onConfirm: doDelete
            }))
        }
    }

    const addNewSegment = (segmentId: string) => {
        disp(editFightActions
            .appendNewSegment({ segmentId }))
    }

    const editSegment = (segmentId: string) => {
        disp(ModalActions.openEditSegmentModal({ segmentId }))
    }

    const addNewEvent = (segmentId: string, event: { name: string; fightOffset: number }) => {
        disp(editFightActions.createNewEvent({
            segmentId,
            name: event.name,
            fightOffset: event.fightOffset
        }))
    }

    const blockChildSegment = (segmentId: string) => {
        const segment = expectOneById(state.segments, segmentId)
        const childSegmentStartOffset = editFightSelectors.getChildSegmentOffset(segment)

        const segmentEvents = state.events.filter(e => e.fightSegmentId === segment.id)
        const segmentEndOffset = resolveSegmentMaxOffset(segment, segmentEvents)

        // If there's no child segment we obviously don't have to
        // block. If there is one, we'll block if there are no seconds
        // between our latest offset and the child segments beginning.
        const blockNewSegment = (childSegmentStartOffset == null)
            ? false
            : (segmentEndOffset >= childSegmentStartOffset)

        return blockNewSegment
    }

    return (
        <div className="pr-3" style={{ width: "650px" }}>

            <div className="d-flex flex-column">
                <div className="d-flex justify-content-between align-items-end mb-3">
                    <div className="d-flex flex-grow-1">
                        <Input
                            data-test={dataTest('edit-fight-name')}
                            className="h4-input"
                            value={fightName}
                            onChange={setFightName}
                            onBlur={setFightNameOnBlur}
                        />
                    </div>
                </div>
            </div>

            <div className={classNames(
                "d-flex p-2 bg-light rounded border",
                "mb-3 justify-content-between align-items-end"
            )}>
                <div className="d-flex w-50 align-items-end space-mx-2">
                    <FormGroup className="mb-0 small flex-grow-1">
                        <Label>{"Fight category"}</Label>
                        <CustomInput
                            data-test={dataTest('edit-fight-category-select')}
                            id="fight-category-select"
                            bsSize="sm"
                            type="select"
                            onChange={e => setFightCategory(e.target.value)}
                            value={fightCategory}
                        >
                            {fightCategoryOptions}
                        </CustomInput>
                    </FormGroup>

                    <Button
                        outline
                        size="sm"
                        onClick={handleSetNote}
                        data-test={dataTest('edit-fight-set-note-btn')}
                        className={"flex-shrink-0"}
                    >
                        {"Set note"}
                    </Button>
                </div>

                <div className="d-flex space-mx-2">
                    <Button
                        color="success"
                        size="sm"
                        onClick={handleSaveChanges}
                        data-test={dataTest('edit-fight-save-changes-btn')}
                    >
                        {"Save changes"}
                    </Button>

                    <Button
                        outline
                        size="sm"
                        onClick={handleCancelEdit}
                        data-test={dataTest('edit-fight-cancel-edit-btn')}
                    >
                        {"Cancel edit"}
                    </Button>
                </div>
            </div>

            {hasAssignments &&
                <div
                    data-test={dataTest('edit-fight-setup-present-alert')}
                    className={"alert-warning small p-2 border mb-3"}
                >
                    {joinLines([
                        `Beware — This fight has one or more setups associated with it.`,
                        ` If you delete events or segments, all of their assignments`,
                        `will be deleted. Modifying events or segments will not delete`,
                        `any assignments.`
                    ])}
                </div>
            }

            <div className="space-my-3">
                <FightSegmentList
                    segments={state.segments}
                    events={state.events}
                    segmentProps={{
                        decorateNameWithOrdinal: (event) => {
                            return decorateNameWithOrdinal(state.events, event)
                        },

                        outlineEditMode: {
                            onAdd: addNewSegment,
                            onEdit: editSegment,

                            onAddEvent: ({ segment, name, seconds, relativeTo }) => {

                                const parseResult = parseNewEvent(
                                    segment,
                                    seconds,
                                    relativeTo,
                                    resolveEventMaxOffset(
                                        editFightSelectors.getChildSegmentOffset(segment)
                                    )
                                )

                                if (typeof parseResult === 'number') {
                                    addNewEvent(segment.id, {
                                        name,
                                        fightOffset: parseResult
                                    })

                                    return undefined
                                }

                                return {
                                    errorMessage: parseResult
                                }
                            },

                            blockChildSegment,
                        },
                        eventProps: {
                            outlineEditMode: {
                                onEdit: (eventId) => {
                                    const event = expectOneById(state.events, eventId)

                                    const segment = expectOneById(
                                        state.segments,
                                        event.fightSegmentId
                                    )

                                    handleEditEvent(segment, event)
                                },
                                onDelete: (eventId, skipConfirm) => {
                                    handleDeleteEvent(eventId, skipConfirm)
                                }
                            }
                        }
                    }}
                />
            </div>
        </div>
    )
}

/**
 * Returns the latest offset in the segment, this marks the "end" of a
 * segment in time.
 */
function resolveSegmentMaxOffset(segment: FightSegment, segmentEvents: FightEvent[]) {
    if (segmentEvents.length <= 0) {
        return segment.offset
    }

    const lastEventOffset = lastItem(segmentEvents).offset
    return lastEventOffset
}

/**
 * Checks if the given offset is within a valid range and returns the
 * fight-offset for the event to be created.
 */
export function parseNewEvent(
    segment: FightSegment,
    offset: number,
    relativeTo: "fight" | "segment",

    /**
     * Defaults to some high value so we don't have to deal with an undefined
     * branch
     */
    maxOffsetRelativeToFight = 999999
): string | number {

    // TODO: Parts of this logic is duplicated over in
    // EditEventModal.tsx, this should be extracted and tested
    // somewhere.

    const maxOffsetRelativeToSegment = maxOffsetRelativeToFight - segment.offset

    const tooLateError = () => {
        const maxTs = (relativeTo === "segment")
            ? secondsToTimestamp(maxOffsetRelativeToSegment)
            : secondsToTimestamp(maxOffsetRelativeToFight)

        return `Too late, event must not be later than ${maxTs}`
    }

    const tooEarlyError = () => {
        const minTs = (relativeTo === "segment")
            ? secondsToTimestamp(0)
            : secondsToTimestamp(segment.offset)

        return `Too early, event must not be earlier than ${minTs}`
    }

    const fightOffset = (relativeTo === "segment")
        ? offset + segment.offset
        : offset

    if (fightOffset > maxOffsetRelativeToFight) {
        return tooLateError()
    }

    if (fightOffset < segment.offset) {
        return tooEarlyError()
    }

    return fightOffset
}

export function resolveEventMaxOffset(childSegmentOffset: number | undefined) {
    if (childSegmentOffset == null) {
        return undefined
    }

    return childSegmentOffset
}
